/* eslint-disable id-blacklist */
import {
    StringDataFlowType, BooleanDataFlowType, DateDataFlowType,
    DoubleDataFlowType, DecimalDataFlowType, ShortDataFlowType,
    IntegerDataFlowType, LongDataFlowType, IDataFlowDataType, TimeStampDataFlowType, AnyDataFlowType, ArrayDataFlowType, FloatDataFlowType
} from './dataflow-datatype';
import { ColumnType } from 'app/shared/utils/column-type.util';
import { Column } from '../dataset/column';
import { Dictionary } from 'lodash';
import { Dataset } from 'app/model/dataset/dataset';
import { AzureBlobDataset } from '../dataset/azure-blob.dataset';
import { DataFlowColumn } from '../dataset/dataflowcolumn';
import { Transformation } from 'app/model/dataflow/transformation';
import { Metamodel } from 'app/model/base/model';
import { Owned } from 'app/model//base/named';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { ComplexDataFlowType, ComplexField } from './dataflow-complex-datatype';
import { DataFlowExpression } from './dataflowexpression';
import { LoggerService } from 'app/logger/logger.service';
import { AzurePostgreSqlTableDataset } from '../dataset/azure-postgre-sql-table.dataset';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { MapDataFlowType } from 'worker/app-workers/shared/models/dataflow-datatype';

export class DataFlowTypeConvertor {
    protected static readonly className = 'DataFlowTypeConvertor';

    private static physicalTypeMappingFromOrc: Dictionary<string> = {
        boolean: 'boolean',
        byte: 'byte',
        short: 'short',
        int: 'integer',
        long: 'long',
        float: 'float',
        double: 'double',
        string: 'string',
        date: 'date',
        timestamp: 'timestamp',
        binary: 'binary',
        decimal: 'decimal',
        varchar: 'string',
        char: 'string'
    };

    private static physicalTypeMappingFromParquet: Dictionary<string> = {
        boolean: 'boolean',
        int_8: 'byte',
        int8: 'byte',
        int_16: 'short',
        int16: 'short',
        int_32: 'integer',
        int32: 'integer',
        int_64: 'long',
        int64: 'long',
        int_96: 'timestamp',
        int96: 'timestamp',
        decimal: 'decimal',
        float: 'float',
        double: 'double',
        date: 'date',
        time_micros: 'timestamp',
        timestamp_millis: 'timestamp',
        timestamp_micros: 'timestamp',
        string: 'string',
        utf8: 'string',
        enum: 'string',
        json: 'string',
        binary: 'binary'
    };

    private static physicalTypeMappingFromSQLDW: Dictionary<string> = {
        bit: 'boolean',
        int: 'integer',
        smallint: 'integer',
        tinyint: 'integer',
        bigint: 'long',
        smallmoney: 'decimal',
        money: 'decimal',
        real: 'float',
        float: 'double',
        binary: 'binary',
        varbinary: 'binary',
        char: 'string',
        nchar: 'string',
        varchar: 'string',
        nvarchar: 'string',
        date: 'date',
        datetime: 'timestamp',
        datetime2: 'timestamp',
        smalldatetime: 'timestamp',
        datetimeoffset: 'string',
        time: 'timestamp',
        decimal: 'decimal',
        numeric: 'decimal',
        uniqueidentifier: 'string'
    };

    private static physicalTypeMappingFromSQLAzure: Dictionary<string> = {
        bit: 'boolean',
        int: 'integer',
        smallint: 'integer',
        tinyint: 'integer',
        bigint: 'long',
        binary: 'binary',
        varbinary: 'binary',
        image: 'binary',
        rowversion: 'binary',
        char: 'string',
        nchar: 'string',
        varchar: 'string',
        nvarchar: 'string',
        text: 'string',
        ntext: 'string',
        xml: 'string',
        date: 'date',
        datetime: 'timestamp',
        datetime2: 'timestamp',
        smalldatetime: 'timestamp',
        datetimeoffset: 'string',
        float: 'double',
        real: 'float',
        decimal: 'decimal',
        numeric: 'decimal',
        money: 'decimal',
        smallmoney: 'decimal',
        uniqueidentifier: 'string',
        timestamp: 'binary',
        hierarchyid: 'binary',
        geometry: 'binary',
        geography: 'binary',
        time: 'timestamp'
    };

    private static physicalTypeMappingFromSnowflake: Dictionary<string> = {
        number: 'decimal',
        float: 'double',
        varchar: 'string',
        binary: 'binary',
        boolean: 'boolean',
        date: 'date',
        time: 'timestamp',
        timestamp_ltz: 'timestamp',
        timestamp_ntz: 'timestamp',
        timestamp_tz: 'timestamp',
        array: 'array'
    };

    private static physicalTypeMappingFromPostgreSql: Dictionary<string> = {
        bigint: 'long',
        bit: 'string',
        ['bit varying']: 'string',
        boolean: 'boolean',
        bytea: 'binary',
        date: 'date',
        ['double precision']: 'double',
        integer: 'integer',
        money: 'double',
        numeric: 'decimal',
        real: 'float',
        smallint: 'short',
        time: 'timestamp',
        ['time with time zone']: 'timestamp',
        timestamp: 'timestamp',
        ['timestamp with time zone']: 'timestamp',
        ['time without time zone']: 'timestamp',
        ['timestamp without time zone']: 'timestamp',
        array: 'string[]'
    };

    private static physicalTypeMappingFromMysql: Dictionary<string> = {
        bigint: 'long',
        ['bigint unsigned']: 'decimal',
        bit: 'boolean',
        blob: 'binary',
        bool: 'boolean',
        char: 'string',
        date: 'date',
        datetime: 'timestamp',
        decimal: 'decimal',
        double: 'double',
        ['double precision']: 'decimal',
        enum: 'string',
        float: 'float',
        int: 'integer',
        ['int unsigned']: 'long',
        integer: 'integer',
        ['integer unsigned']: 'long',
        ['long varbinary']: 'binary',
        ['long varchar']: 'string',
        longblob: 'binary',
        longtext: 'string',
        mediumblob: 'binary',
        mediumint: 'integer',
        ['mediumint unsigned']: 'integer',
        mediumtext: 'string',
        numeric: 'decimal',
        real: 'double',
        set: 'string',
        smallint: 'integer',
        ['smallint unsigned']: 'integer',
        text: 'string',
        time: 'timestamp',
        timestamp: 'timestamp',
        tinyblob: 'binary',
        tinyint: 'integer',
        ['tinyint unsigned']: 'integer',
        tinytext: 'string',
        varchar: 'string',
        year: 'integer'
    };

    private static physicalTypeMappingFromAzureDataExplorer = {
        string: 'string',
        long: 'long',
        bool: 'boolean',
        real: 'double',
        int: 'integer',
        decimal: 'decimal',
        datetime: 'timestamp',
        timespan: 'string',
        guid: 'string',
        dynamic: 'string'
    };

    private static physicalTypeMappingFromDynamics = {
        boolean: 'boolean',
        customer: 'string',
        datatime: 'timestamp',
        decimal: 'decimal',
        double: 'double',
        integer: 'integer',
        lookup: 'string',
        memo: 'string',
        money: 'decimal',
        owner: 'string',
        partylist: 'array',
        picklist: 'integer',
        state: 'integer',
        status: 'integer',
        string: 'string',
        uniqueidentifier: 'string',
        bigint: 'long',
        managedproperty: 'boolean',
        entityname: 'string'
    };

    private static physicalTypeMappingFromDynamicsCRM = DataFlowTypeConvertor.physicalTypeMappingFromDynamics;

    private static typeListMapping: Dictionary<Dictionary<string>> = {
        [adfModelTypeMap.parquet.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromParquet,
        [adfModelTypeMap.azureSqlDW.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromSQLDW,
        [adfModelTypeMap.azureSql.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromSQLAzure,
        [adfModelTypeMap.snowflake.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromSnowflake,
        [adfModelTypeMap.snowflakeV2.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromSnowflake,
        [adfModelTypeMap.azurePostgreSQL.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromPostgreSql,
        [adfModelTypeMap.azureSqlMI.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromSQLAzure,
        [adfModelTypeMap.azureMySql.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromMysql,
        [adfModelTypeMap.sqlServer.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromSQLAzure,
        [adfModelTypeMap.azureDataExplorer.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromAzureDataExplorer,
        [adfModelTypeMap.dynamics.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromDynamics,
        [adfModelTypeMap.dynamicsCRM.datasetType]: DataFlowTypeConvertor.physicalTypeMappingFromDynamicsCRM
    };

    public static convertDatasetColumnsToDataFlowColumns(dataset: Dataset, transformation: Transformation, services: { loggerService: LoggerService }): DataFlowColumn[] {
        const blobSource = dataset as AzureBlobDataset;
        const makeAllColumnsString = Metamodel.modelOf(dataset).name === AzureBlobDataset.MODEL.name
            && blobSource && blobSource.format && blobSource.format.type === 'TextFormat';
        if (dataset.rawHierarchicalSchema) {
            return this._hierarchicalSchemaToColumns(dataset.rawHierarchicalSchema, transformation);
        } else {
            return dataset.columns.map((column: Column, idx: number) => {
                const dataFlowColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
                dataFlowColumn.name = column.name;
                if (!dataFlowColumn.name) {
                    dataFlowColumn.name = `Column_${idx + 1}`;
                }
                // Convert from dataset column type.
                dataFlowColumn.dataFlowColumnType = makeAllColumnsString ? new StringDataFlowType() : DataFlowTypeConvertor.convertToSparkType(column, dataset, services);
                return dataFlowColumn;
            });
        }
    }

    public static convertToSparkType(datasetColumn: Column, sourceDataset: Dataset, services: { loggerService: LoggerService }): IDataFlowDataType {
        const datasetType = sourceDataset && sourceDataset.isV2 ? Metamodel.modelOf(sourceDataset).name : undefined;
        if (datasetType === 'LakehouseTable') {
            const colType = DataFlowTypesUtils.createType(datasetColumn.columnType);
            if (colType instanceof DecimalDataFlowType) {
                colType.precision = datasetColumn.precision;
                colType.scale = datasetColumn.scale;
            }
            return colType;
        }
        return this.convertToSparkTypeByColumnString(datasetColumn.columnType, datasetType, datasetColumn.precision, datasetColumn.scale, services);
    }

    public static convertToSparkTypeByColumnString(columnType: string, datasetType: string, precision: number, scale: number, services: { loggerService: LoggerService }): IDataFlowDataType {
        if (!columnType) {
            return new StringDataFlowType();
        }

        if (datasetType) {
            const typeList = this.typeListMapping[datasetType];

            if (typeList) {
                // some datatypes are in the format <dbName>.sys.<dataType> e.g., ceespinoSql.sys.geography, ceespinoSql.sys.geometry, ceespinoSql.sys.hierarchyid
                const datasetColumnType = columnType.substr(columnType.lastIndexOf('.') + 1).toLowerCase();
                let dataType;
                if (datasetType === adfModelTypeMap.azureMySql.datasetType && /^bit\(\d+\)$/.test(datasetColumnType)) {
                    const num = parseInt(datasetColumnType.substring(4, datasetColumnType.length - 1), 10);
                    dataType = num > 1 ? DataFlowTypesUtils.createType('binary') : DataFlowTypesUtils.createType('boolean');
                } else {
                    dataType = DataFlowTypesUtils.createType(typeList[datasetColumnType]);
                }

                if (!dataType) {
                    dataType = datasetType === Metamodel.modelFor(AzurePostgreSqlTableDataset).name ? new StringDataFlowType() : new AnyDataFlowType();
                    // This is consumed by an external application where the injector does not exist. That's why we do a null check.
                    if (services?.loggerService) {
                        services.loggerService.warn(DataFlowTypeConvertor.className, `Failed to convert type: ${datasetColumnType}, Dataset type: ${datasetType}`);
                    }
                }

                if (dataType instanceof DecimalDataFlowType) {
                    if (datasetColumnType === 'money') {
                        dataType.precision = 19;
                        dataType.scale = 4;
                    } else if (datasetColumnType === 'smallmoney') {
                        dataType.precision = 10;
                        dataType.scale = 4;
                    } else if (datasetType === adfModelTypeMap.azureDataExplorer.datasetType) {
                        dataType.precision = 20;
                        dataType.scale = 14;
                    } else {
                        dataType.precision = precision;
                        dataType.scale = scale;
                    }
                }
                return dataType;
            }
        }

        switch (columnType) {
            case ColumnType.String:
                return new StringDataFlowType();
            case ColumnType.Int64:
                return new LongDataFlowType();
            case ColumnType.Int32:
                return new IntegerDataFlowType();
            case ColumnType.Int16:
                return new ShortDataFlowType();
            case ColumnType.Decimal:
                return new DecimalDataFlowType();
            case ColumnType.Double:
                return new DoubleDataFlowType();
            case ColumnType.DateTime:
                return new TimeStampDataFlowType();
            case ColumnType.Date:
                return new DateDataFlowType();
            case ColumnType.Boolean:
                return new BooleanDataFlowType();
            default:
                return new StringDataFlowType();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _hierarchicalSchemaToColumns(hierarchicalSchema: any, transformation: Transformation): DataFlowColumn[] {
        const dataFlowColumns: DataFlowColumn[] = [];
        if (Array.isArray(hierarchicalSchema)) {
            DataFlowTypeConvertor._hierarchicalOrcSchemaToColumns(hierarchicalSchema, transformation, dataFlowColumns);
        } else if (hierarchicalSchema.properties) {
            DataFlowTypeConvertor._hierarchicalJsonSchemaToColumns(hierarchicalSchema.properties, transformation, dataFlowColumns);
        } else if (hierarchicalSchema.fields) {
            DataFlowTypeConvertor._hierarchicalAvroSchemaToColumns(hierarchicalSchema.fields, transformation, dataFlowColumns);
        }
        return dataFlowColumns;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _hierarchicalJsonSchemaToColumns(fields: any, transformation: Transformation, columns: DataFlowColumn[]): void {
        // For now this is being coded for the JSON dataset.
        Object.keys(fields).forEach((key) => {
            const dataFlowColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
            dataFlowColumn.name = key;
            dataFlowColumn.dataFlowColumnType = this._getHierarchicalType(fields[key]);
            columns.push(dataFlowColumn);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _hierarchicalAvroSchemaToColumns(fields: any[], transformation: Transformation, columns: DataFlowColumn[]): void {
        // For now this is being coded for the AVRO dataset.
        fields.forEach((column) => {
            const dataFlowColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
            dataFlowColumn.name = column.name;
            dataFlowColumn.dataFlowColumnType = this._getHierarchicalType(column);
            columns.push(dataFlowColumn);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _hierarchicalOrcSchemaToColumns(fields: any[], transformation: Transformation, columns: DataFlowColumn[]): void {
        // For now this is being coded for the AVRO dataset.
        fields.forEach((column) => {
            const dataFlowColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
            dataFlowColumn.name = column.name;
            dataFlowColumn.dataFlowColumnType = this._getHierarchicalType(column, this.physicalTypeMappingFromOrc);
            columns.push(dataFlowColumn);
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _getHierarchicalType(columnTypeDefinition: any, typeMappingList?: { [key: string]: string }): IDataFlowDataType {
        if (columnTypeDefinition == null) {
            // default is string
            return new StringDataFlowType();
        }
        if (Array.isArray(columnTypeDefinition.type) && columnTypeDefinition.type[0] && typeof columnTypeDefinition.type[0] === 'object' && columnTypeDefinition.type[0].type) {
            // Avro dataset's types sometimes are arrays, take the first element of the array as the type definition
            columnTypeDefinition = columnTypeDefinition.type[0];
        }
        if (typeof columnTypeDefinition.type === 'object' && columnTypeDefinition.type.type) {
            // Avro dataset's types sometimes are objects which contain the actual type name whitin the type object
            columnTypeDefinition = columnTypeDefinition.type;
        }
        const columnType = typeof columnTypeDefinition.type === 'string' ? columnTypeDefinition.type.toLowerCase() :
            Array.isArray(columnTypeDefinition.type) ? columnTypeDefinition.type[0] : columnTypeDefinition.type;
        if (typeof columnType === 'string' && typeMappingList && typeMappingList[columnType]) {
            return DataFlowTypesUtils.createType(typeMappingList[columnType]);
        }
        switch (columnType) {
            case 'string':
                return new StringDataFlowType();
            case 'number':
                return new DoubleDataFlowType();
            case 'long':
                return new LongDataFlowType();
            case 'int':
            case 'integer':
                return new IntegerDataFlowType();
            case 'bytes':
                return new ShortDataFlowType();
            case 'float':
                return new FloatDataFlowType();
            case 'double':
                return new DoubleDataFlowType();
            case 'boolean':
                return new BooleanDataFlowType();
            case 'object':
            case 'struct':
                const complexDataFlowType = new ComplexDataFlowType();
                if (columnTypeDefinition.properties && Object.keys(columnTypeDefinition.properties).length) {
                    Object.keys(columnTypeDefinition.properties).forEach((key) => {
                        const dataFlowExpression = new DataFlowExpression();
                        dataFlowExpression.returnType = this._getHierarchicalType(columnTypeDefinition.properties[key]);
                        complexDataFlowType.fields.push(new ComplexField(key, dataFlowExpression, complexDataFlowType));
                    });
                } else {
                    const dataFlowExpression = new DataFlowExpression();
                    dataFlowExpression.returnType = new StringDataFlowType();
                    complexDataFlowType.fields.push(new ComplexField('', dataFlowExpression, complexDataFlowType));
                }
                return complexDataFlowType;
            case 'record':
                const complexAvroDataFlowType = new ComplexDataFlowType();
                if (columnTypeDefinition.fields && columnTypeDefinition.fields.length) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    columnTypeDefinition.fields.forEach((field: any) => {
                        const dataFlowExpression = new DataFlowExpression();
                        dataFlowExpression.returnType = this._getHierarchicalType(field);
                        complexAvroDataFlowType.fields.push(new ComplexField(field.name, dataFlowExpression, complexAvroDataFlowType));
                    });
                }
                return complexAvroDataFlowType;
            case 'array':
            case 'list':
                const arrayDataFlowType = new ArrayDataFlowType();
                arrayDataFlowType.arrayType = this._getHierarchicalType(columnTypeDefinition.items);
                return arrayDataFlowType;
            case 'map':
                const mapDataFlowType = new MapDataFlowType(new StringDataFlowType(), new StringDataFlowType());
                if (columnTypeDefinition.key) {
                    mapDataFlowType.mapKeyType = this._getHierarchicalType(columnTypeDefinition.key);
                }
                if (columnTypeDefinition.value) {
                    mapDataFlowType.mapValueType = this._getHierarchicalType(columnTypeDefinition.value);
                } else if (columnTypeDefinition.values) {
                    mapDataFlowType.mapValueType = Array.isArray(columnTypeDefinition.values) ?
                        this._getHierarchicalType(columnTypeDefinition.values[0]) :
                        this._getHierarchicalType(columnTypeDefinition.values);
                }
                return mapDataFlowType;
            default:
                return new StringDataFlowType();
        }
    }
}
